import i18n from 'i18next';

import store from 'store/store';
import { fetchUserBalance } from 'store/user-service/asyncActions';
import { setNotEnoughTokensError } from 'store/user-service/userSlice';

import { baseURL } from 'utils/API';
import chatGptInstance from 'utils/ChatGPT.axios';
import { chatGptFetch } from 'utils/ChatGPT.fetch';
import { escapeNewLinesInString } from 'utils/escapeNewLinesInString';
import { normalizeObjectKeys } from 'utils/normalizeObjectKeys';
import { VaultService } from 'utils/storage';
import { string_between_strings } from 'utils/substringBetweenStrings';

import { notEnoughTokensErrorMessage } from 'core/constants/chatGptRequestRegenerateMessages';
import {
	ChatGPTModels,
	ChatGPTRagTemplateNames,
	ChatGPTStreamingTemplateNames,
	ChatGPTSubStageTemplateNames,
	ChatGPTTemplateNames,
	mergedTemplateType,
} from 'core/enums/common';

import {
	IUseCase,
	IUseCaseResponse,
	ResponseBanner,
	ResponseDescription,
	ResponseNotification,
} from './types';

// difference between getCustomData, GetCustomDataGptFour, getCustomDataWithFormatting
// can be found in readme file in API and backend communication section as well as input parameters explanation.

export const handleNotEnoughTokensError = (err: any) => {
	if (err.message === notEnoughTokensErrorMessage) {
		store.dispatch(setNotEnoughTokensError(true));
		throw new Error(notEnoughTokensErrorMessage);
	}
};

export const getCustomData = async ({
	templateName,
	userInput,
	temperature = 0,
	aiModelEnum = ChatGPTModels['GPT3.5-turbo'],
	modelGroup,
	ragTemplate = ChatGPTRagTemplateNames['RAG'],
	fileName,
}: {
	templateName: string;
	userInput: string;
	temperature?: number;
	aiModelEnum?: ChatGPTModels;
	modelGroup?: mergedTemplateType;
	ragTemplate?: ChatGPTRagTemplateNames;
	fileName?: string;
}) => {
	const requestModel = modelGroup
		? store.getState().userRedux.aiRequestsModelsConfiguration[modelGroup]
		: aiModelEnum;

	const defaultTemplate = {
		aiModelEnum: requestModel,
		userInput,
		templateName,
		temperature,
	};

	const activeTicket = store.getState().ticketRedux.activeTicket;
	const activeStep = store.getState().ticketRedux.activeStep;

	const response: any = await chatGptInstance
		.post(
			'/Communication/getChatResponse',
			fileName ? { ...defaultTemplate, fileName, useRAG: true, ragTemplate } : defaultTemplate,
			{
				params: {
					ticketOwnerId: activeTicket?.ticketOwnerId || '',
					ticketId: activeTicket?.id || '',
					ticketCurrentStep: activeStep.toString(),
				},
			}
		)
		.then((response) => {
			store.dispatch(fetchUserBalance());
			return response;
		})
		.catch(async (err) => {
			if (err.status === 499) {
				return getCustomData({
					templateName,
					userInput,
					temperature,
					aiModelEnum,
					fileName,
					ragTemplate,
				});
			} else {
				handleNotEnoughTokensError({ message: err.data });
			}
		});

	let finalResponse = response?.headers ? response?.data : response;
	
	if (typeof finalResponse === 'string') {
		
		// Extraire le JSON des balises markdown ```json ... ```
		const jsonMatch = finalResponse.match(/```json\s*\n([\s\S]*?)\n```/);
		if (jsonMatch && jsonMatch[1]) {
			try {
				finalResponse = JSON.parse(jsonMatch[1]);
			} catch (e) {
				console.error('Failed to parse JSON from markdown:', e);
			}
		} else {
			try {
				finalResponse = JSON.parse(finalResponse);
			} catch (e) {
				console.log('Not valid JSON, returning as string');
			}
		}
	}
	return finalResponse;
};

export const getCustomDataWithFormatting = async ({
	templateName,
	userInput,
	fileName,
	conversationHistory,
	temperature = 0,
	aiModelEnum = ChatGPTModels['GPT4'],
	modelGroup,
	ragTemplate = ChatGPTRagTemplateNames['RAG'],
}: {
	templateName: string;
	userInput: string;
	fileName?: string;
	temperature?: number;
	conversationHistory?: Record<string, string>;
	aiModelEnum?: ChatGPTModels;
	modelGroup?: mergedTemplateType;
	ragTemplate?: ChatGPTRagTemplateNames;
}) => {
	const requestModel = modelGroup
		? store.getState().userRedux.aiRequestsModelsConfiguration[modelGroup]
		: aiModelEnum;

	const defaultTemplate = {
		aiModelEnum: requestModel,
		userInput,
		templateName,
		temperature,
		conversationHistory,
	};

	const activeTicket = store.getState().ticketRedux.activeTicket;
	const activeStep = store.getState().ticketRedux.activeStep;

	const response: any = await chatGptInstance
		.post(
			'/Communication/chatWithFunctions',
			fileName ? { ...defaultTemplate, fileName, useRAG: true, ragTemplate } : defaultTemplate,
			{
				params: {
					ticketOwnerId: activeTicket?.ticketOwnerId || '',
					ticketId: activeTicket?.id || '',
					ticketCurrentStep: activeStep.toString(),
				},
			}
		)
		.catch(async (err) => {
			if (err?.status === 499) {
				return getCustomDataWithFormatting({
					templateName,
					userInput,
					fileName,
					conversationHistory,
					temperature,
					aiModelEnum,
					modelGroup,
					ragTemplate,
				});
			} else {
				handleNotEnoughTokensError({ message: err?.data });
			}
		});

	return response?.headers ? response?.data : response;
};

// getConversation is used to get chatGPT response on user's message. It is used only on general step.
// Here, conversation is string that contains all previous messages from user and AI.

interface IConversationResponse {
	response: string;
	isConversationEnded: boolean;
	isMessageValid: boolean;
}

export const getConversation = async ({
	conversation,
	conversationObject,
	userMessage,
	templateName,
	fileName,
}: {
	conversation: string;
	conversationObject?: Record<string, string>;
	userMessage: string;
	templateName: string;
	fileName?: string;
}): Promise<IConversationResponse> => {
	const defaultConversationRequestInput = {
		templateName: templateName,
		ragTemplate: ChatGPTRagTemplateNames['chatRAGTemplate'],
		temperature: 0.9,
		modelGroup: ChatGPTSubStageTemplateNames['FeaturesConversationTemplate'],
		fileName,
	};

	const conversationRequestInput =
		/*conversationObject && Object.keys(conversationObject).length
			? {
					...defaultConversationRequestInput,
					conversationHistory: conversationObject,
					userInput: `${i18n.t('common:modelsInput.currentMessage')}: ${userMessage}`,
			  }
			: {
					...defaultConversationRequestInput,
					userInput: `${i18n.t('common:modelsInput.chatHistory')}: ${conversation}. ${i18n.t(
						'common:modelsInput.userMessage'
					)}: ${userMessage}`,
			  };*/

			  conversationObject && Object.keys(conversationObject).length
			  ? {
					  ...defaultConversationRequestInput,
					  conversationHistory: conversationObject,
					  userInput: userMessage, // Seulement le message utilisateur, sans labels i18n
				}
			  : {
					  ...defaultConversationRequestInput,
					  userInput: `${i18n.t('common:modelsInput.chatHistory')}: ${conversation}. ${i18n.t(
						  'common:modelsInput.userMessage'
					  )}: ${userMessage}`,
				};
	
	const response = await getCustomDataWithFormatting(conversationRequestInput).catch((err) => {
		handleNotEnoughTokensError(err);

		return {
			response: '',
			isConversationEnded: false,
			isMessageValid: true,
		};
	});

	// sometimes AI returns inconsistent keys casing. So I normalize response object keys to lower case.
	const parsedResponse = typeof response === 'string' ? escapeNewLinesInString(response) : response;
	let normalizedResponse: any;
	if (typeof parsedResponse === 'string') {
		const technicalFieldsRegex = /^(IsEndConversation|IsConversationEnded|IsMessageValid|true|false)$/i;
		if (technicalFieldsRegex.test(parsedResponse.trim())) {
			// This is a malformed response containing only technical field names
			normalizedResponse = {
				response: '',
				isendconversation: parsedResponse.toLowerCase().includes('isendconversation') || parsedResponse.toLowerCase().includes('true'),
				ismessagevalid: true,
			};
		} else {
			normalizedResponse = {
				response: parsedResponse,
				isendconversation: false,
				ismessagevalid: true,
			};
		}
	} else {
		normalizedResponse = normalizeObjectKeys(parsedResponse);
	}

	const isConversationEnded =
		typeof normalizedResponse['isendconversation'] === 'string'
			? normalizedResponse['isendconversation'].toLowerCase() === 'true'
			: normalizedResponse['isendconversation'];

	const isMessageValid =
		typeof normalizedResponse['ismessagevalid'] === 'string'
			? normalizedResponse['ismessagevalid'].toLowerCase() === 'true'
			: normalizedResponse['ismessagevalid'];

	// for some reason GPT4-turbo sends this field with another name
	const isConversationEndedBackUp =
		typeof normalizedResponse['isconversationended'] === 'string'
			? normalizedResponse['isconversationended'].toLowerCase() === 'true'
			: normalizedResponse['isconversationended'];

	let finalResponse = normalizedResponse['response'] || '';

	// Filter out technical field names that shouldn't be shown to users
	// Sometimes the backend returns field names instead of actual content
	const technicalFieldsRegex = /\b(IsEndConversation|IsConversationEnded|IsMessageValid|isendconversation|isconversationended|ismessagevalid)\b/gi;
	if (typeof finalResponse === 'string' && technicalFieldsRegex.test(finalResponse)) {
		// If the response only contains technical fields, treat it as empty
		if (finalResponse.replace(technicalFieldsRegex, '').trim().length === 0) {
			finalResponse = '';
		} else {
			// Otherwise, just remove the technical fields from the response
			finalResponse = finalResponse.replace(technicalFieldsRegex, '').trim();
		}
	}

	if (finalResponse) {
		const spanishOnlyWords = /\b(hola|gracias|puedes|deseas|necesitas|ayuda|asistir|compañía|proyecto|requisitos|necesidades|mejor|decir|industria|saludar|asistirte|puedo|empresa|hablamos|ayudarte|específicamente|mensaje|saber|qué|cómo|cuál|poder|más|con|estoy|aquí|tengo|quiero|hacer|sobre|pero|porque|cuando|donde|también|ahora|siempre|nunca|nada|todo|muy|bien|mal|bueno|malo|grande|pequeño|nuevo|viejo)\b/i;

		const spanishSpecificChars = /[¿¡]/;
		
		const isSpanish = spanishOnlyWords.test(finalResponse) || spanishSpecificChars.test(finalResponse);

		if (isSpanish) {
			// Traduire la réponse espagnole en anglais
			try {
				const translationResponse = await getCustomData({
					templateName: ChatGPTTemplateNames.DescriptionTemplate,
					userInput: `Translate the following Spanish text to English. Return only the English translation, nothing else:\n\n${finalResponse}`,
					aiModelEnum: ChatGPTModels['GPT3.5-turbo'],
					temperature: 0,
					fileName,
				});

				// Extraire la traduction de la réponse
				if (translationResponse) {
					if (typeof translationResponse === 'string') {
						finalResponse = translationResponse.trim();
					} else if (typeof translationResponse === 'object') {
						finalResponse = (
							translationResponse.description ||
							translationResponse.title ||
							translationResponse.response ||
							finalResponse
						).trim();
					}
				}
			} catch (err) {
				console.error('Error translating Spanish response:', err);
			}
		}
	}

	return {
		response: finalResponse,
		isConversationEnded: isConversationEnded || isConversationEndedBackUp,
		isMessageValid: isMessageValid,
	};
};

export const getUserStoryScenario = async ({
	userStory,
	fileName,
}: {
	userStory: string;
	fileName?: string;
}) => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.UserStoriesScenariosTemplate,
		userInput: `${i18n.t('common:modelsInput.story')}: ${userStory}`,
		aiModelEnum: ChatGPTModels['GPT4'],
		modelGroup: ChatGPTStreamingTemplateNames['PotentialSummaryTemplate'],
		fileName,
	});

	return response?.scenarios || 'Not generated';
};

export const getUserStoryAcceptanceCriteria = async ({
	userStoryWithScenarios,
	fileName,
}: {
	userStoryWithScenarios: string;
	fileName?: string;
}) => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.UserStoriesAcceptanceCriteriaTemplate,
		userInput: userStoryWithScenarios,
		aiModelEnum: ChatGPTModels['GPT4'],
		modelGroup: ChatGPTStreamingTemplateNames['PotentialSummaryTemplate'],
		fileName,
	});

	return response?.acceptanceCriteria;
};

// getIsUserMessageValid is used to validate user's message before using it in getConversation function.
// Validation rules can be found in IsUserMessageValidTemplate in swagger.

export const getIsUserMessageValid = async ({
	chatQuestion,
	message,
	isStrict,
	fileName,
}: {
	chatQuestion: string;
	message: string;
	isStrict: boolean;
	fileName?: string;
}): Promise<boolean> => {
	const response = await getCustomData({
		templateName: ChatGPTTemplateNames.IsUserMessageValidTemplate,
		userInput: `${
			chatQuestion.length ? `${i18n.t('common:modelsInput.question')}: ${chatQuestion}. ` : ''
		}. ${i18n.t('common:modelsInput.message')}: ${message}. ${i18n.t(
			'common:modelsInput.isStrict'
		)}: ${isStrict}.`,
		aiModelEnum: ChatGPTModels['GPT4o'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return false;
	});

	return typeof response === 'boolean' ? response : response.toLowerCase() === 'true';
};

// getUsecaseConversation is used to get chatGPT response on user's request. It is used only on usecase step.

export const getUsecaseConversation = async ({
	chatHistory,
	userValue,
	fileName,
}: {
	chatHistory: string;
	userValue: string;
	fileName?: string;
}): Promise<string> => {
	return await getCustomData({
		templateName: ChatGPTTemplateNames.UsecaseConversationTemplate,
		ragTemplate: ChatGPTRagTemplateNames['chatRAGTemplate'],
		userInput: `${i18n.t('common:modelsInput.conversation')}: ${chatHistory}. ${i18n.t(
			'common:modelsInput.message'
		)}: ${userValue}.`,
		temperature: 0.9,
		aiModelEnum: ChatGPTModels['GPT4-turbo'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return '';
	});
};

// getNotificationsConversation is used to get chatGPT response on user's request. It is used only on notifications step.

export const getNotificationsConversation = async ({
	chatHistory,
	userValue,
	fileName,
}: {
	chatHistory: string;
	userValue: string;
	fileName?: string;
}): Promise<string> => {
	return await getCustomData({
		templateName: ChatGPTTemplateNames.NotificationsConversationTemplate,
		ragTemplate: ChatGPTRagTemplateNames['chatRAGTemplate'],
		userInput: `${i18n.t('common:modelsInput.conversation')}: ${chatHistory}. ${i18n.t(
			'common:modelsInput.message'
		)}: ${userValue}.`,
		temperature: 0.9,
		aiModelEnum: ChatGPTModels['GPT4-turbo'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return '';
	});
};

// getDescription is used to get description based on user stories that will be displayed on general step.

export const getDescription = async ({
	stories,
	fileName,
}: {
	stories: string;
	fileName?: string;
}): Promise<ResponseDescription> => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.DescriptionTemplate,
		userInput: `${i18n.t('common:modelsInput.userStories')}: ${stories}`,
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return {
			title: '',
			description: '',
		};
	});

	return typeof response === 'string' ? JSON.parse(response) : response;
};

// getDiagramCorrection is used to correct plantUML code of diagram based on requirements from chat.

export const getDiagramCorrection = async ({
	diagramCode,
	diagramType,
	requirements,
	fileName,
}: {
	diagramCode: string;
	diagramType: string;
	requirements: string;
	fileName?: string;
}) => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.DiagramCorrectionTemplate,
		userInput: `${i18n.t('common:modelsInput.code')}: ${diagramCode}. ${i18n.t(
			'common:modelsInput.diagramType'
		)}: ${diagramType}. ${i18n.t('common:modelsInput.requirements')}: ${requirements}.`,
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return '';
	});

	if (typeof response === 'string') {
		return string_between_strings('@startuml', '@enduml', response);
	} else {
		return response['Code'];
	}
};

export const getUseCaseTestPlan = async ({
	useCaseTitle,
	useCaseDescription,
	relatedUserStories,
	fileName,
}: {
	useCaseTitle: string;
	useCaseDescription: string;
	relatedUserStories: string;
	fileName?: string;
}) => {
	return await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.TestPlanTemplate,
		userInput: `${i18n.t('common:modelsInput.useCase')}: ${useCaseTitle}. ${i18n.t(
			'common:modelsInput.useCaseDescription'
		)}: ${useCaseDescription}. ${i18n.t('common:modelsInput.userStories')}: ${relatedUserStories}`,
		aiModelEnum: ChatGPTModels['GPT4'],
		modelGroup: ChatGPTTemplateNames['TestPlanTemplate'],
		fileName,
	});
};

export const getUseCaseTestScenarios = async ({
	useCaseTitle,
	useCaseDescription,
	relatedUserStoriesWithScenarios,
	fileName,
}: {
	useCaseTitle: string;
	useCaseDescription: string;
	relatedUserStoriesWithScenarios: string;
	fileName?: string;
}) => {
	return await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.TestScenariosTemplate,
		userInput: `${i18n.t('common:modelsInput.useCase')}: ${useCaseTitle}. ${i18n.t(
			'common:modelsInput.useCaseDescription'
		)}: ${useCaseDescription}. ${i18n.t(
			'common:modelsInput.userStories'
		)}: ${relatedUserStoriesWithScenarios}`,
		aiModelEnum: ChatGPTModels['GPT4'],
		modelGroup: ChatGPTTemplateNames['TestPlanTemplate'],
		fileName,
	});
};

export const getUseCaseTestCases = async ({
	useCaseTitle,
	useCaseDescription,
	testScenario,
	relatedUserStoriesWithScenarios,
	fileName,
}: {
	useCaseTitle: string;
	useCaseDescription: string;
	testScenario: string;
	relatedUserStoriesWithScenarios: string;
	fileName?: string;
}) => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.TestCasesTemplate,
		userInput: `${i18n.t('common:modelsInput.useCase')}: ${useCaseTitle}. ${i18n.t(
			'common:modelsInput.useCaseDescription'
		)}: ${useCaseDescription}. ${i18n.t(
			'common:modelsInput.testScenarios'
		)}: ${testScenario}. ${i18n.t(
			'common:modelsInput.userStories'
		)}: ${relatedUserStoriesWithScenarios}`,
		aiModelEnum: ChatGPTModels['GPT4'],
		modelGroup: ChatGPTTemplateNames['TestPlanTemplate'],
		fileName,
	});

	return response?.testCase || response;
};

// getPotentialNotifications is used to get potential notifications based on system description. Notifications will be displayed on notifications step.

export const getPotentialNotifications = async ({
	description,
	fileName,
}: {
	description: string;
	fileName?: string;
}): Promise<Array<ResponseNotification>> => {
	const response = await getCustomData({
		templateName: ChatGPTTemplateNames.PotentialNotificationsTemplate,
		userInput: `${i18n.t('common:modelsInput.projectDescription')}: ${description}`,
		aiModelEnum: ChatGPTModels['GPT3.5-turbo'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return { notifications: [] };
	});
	
	// Si c'est déjà un tableau, le retourner directement
	if (Array.isArray(response)) {
		return response;
	}

	// Sinon, extraire la propriété notifications
	const notifications = response?.notifications;
	
	return notifications || [];
};

export const getNotifications = async ({
	suggestions,
	fileName,
}: {
	suggestions: string;
	fileName?: string;
}): Promise<Array<ResponseNotification>> => {
	const response = await getCustomData({
		templateName: ChatGPTTemplateNames.NotificationsTemplate,
		userInput: `${i18n.t('common:modelsInput.suggestions')}: ${suggestions}`,
		aiModelEnum: ChatGPTModels['GPT3.5-turbo'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return {
			title: '',
			description: '',
		};
	});

	return response?.notifications || [response];
};

// getDiagramValidation fixes syntax errors in plantUML code of diagram in case initial diagram could not be loaded.

export const getDiagramValidation = async ({
	code,
	fileName,
}: {
	code: string;
	fileName?: string;
}): Promise<string> => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.DiagramValidationTemplate,
		userInput: `${i18n.t('common:modelsInput.code')}: ${code}`,
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return '';
	});

	if (typeof response === 'string') {
		return string_between_strings('@startuml', '@enduml', response);
	} else {
		return response['correctedCode'];
	}
};

export const getUserStoriesEpics = async ({
	userStories,
	fileName,
}: {
	userStories: string;
	fileName?: string;
}): Promise<Array<IUseCaseResponse>> => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.UserStoriesEpicsTemplate,
		userInput: `${i18n.t('common:modelsInput.userStories')}: ${userStories}`,
		aiModelEnum: ChatGPTModels['GPT4-32K'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return [];
	});

	return response['epics'];
};

export const getBusinessImpact = async ({
	userStories,
	fileName,
}: {
	userStories: string;
	fileName?: string;
}) => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.BusinessImpactTemplate,
		userInput: `${i18n.t('common:modelsInput.userStories')}: ${userStories}`,
		modelGroup: ChatGPTTemplateNames['UserStoriesEpicsTemplate'],
		fileName,
	})
		.then((res) => {
			store.dispatch(fetchUserBalance());
			return res;
		})
		.catch((err) => {
			handleNotEnoughTokensError(err);

			return [];
		});

	return typeof response === 'string'
		? JSON.parse(response)['BusinessImpact']
		: response['BusinessImpact'];
};

export const getKeyRoles = async ({
	userStories,
	useCase,
	fileName,
}: {
	userStories: string;
	useCase: IUseCase;
	fileName?: string;
}) => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.KeyRolesTableTemplate,
		userInput: `${i18n.t('common:modelsInput.projectDescription')}: ${userStories}. ${i18n.t(
			'common:modelsInput.useCase'
		)}: ${useCase.title}. ${i18n.t('common:modelsInput.useCaseDescription')}: ${
			useCase.description
		}.`,
		modelGroup: ChatGPTTemplateNames['UserStoriesEpicsTemplate'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return [];
	});

	return typeof response === 'string' ? JSON.parse(response)['Roles'] : response['Roles'];
};

export const getPreconditions = async ({
	userStories,
	useCase,
	fileName,
}: {
	userStories: string;
	useCase: IUseCase;
	fileName?: string;
}) => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.PreconditionsTableTemplate,
		userInput: `${i18n.t('common:modelsInput.projectDescription')}: ${userStories}. ${i18n.t(
			'common:modelsInput.useCase'
		)}: ${useCase.title}. ${i18n.t('common:modelsInput.useCaseDescription')}: ${
			useCase.description
		}.`,
		modelGroup: ChatGPTTemplateNames['UserStoriesEpicsTemplate'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return [];
	});

	return typeof response === 'string' ? JSON.parse(response)['Conditions'] : response['Conditions'];
};

export const getTrigger = async ({
	userStories,
	useCase,
	fileName,
}: {
	userStories: string;
	useCase: IUseCase;
	fileName?: string;
}) => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.TriggerTableTemplate,
		userInput: `${i18n.t('common:modelsInput.projectDescription')}: ${userStories}. ${i18n.t(
			'common:modelsInput.useCase'
		)}: ${useCase.title}. ${i18n.t('common:modelsInput.useCaseDescription')}: ${
			useCase.description
		}.`,
		modelGroup: ChatGPTTemplateNames['UserStoriesEpicsTemplate'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return [];
	});

	return typeof response === 'string' ? JSON.parse(response)['Trigger'] : response['Trigger'];
};

export const getSuccessfulOutcome = async ({
	userStoriesAcceptanceCriteria,
	useCase,
	fileName,
}: {
	userStoriesAcceptanceCriteria: string;
	useCase: IUseCase;
	fileName?: string;
}) => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.SuccessfulOutcomeTableTemplate,
		userInput: `${i18n.t(
			'common:modelsInput.projectDescription'
		)}: ${userStoriesAcceptanceCriteria}. ${i18n.t('common:modelsInput.useCase')}: ${
			useCase.title
		}. ${i18n.t('common:modelsInput.useCaseDescription')}: ${useCase.description}.`,
		modelGroup: ChatGPTTemplateNames['UserStoriesEpicsTemplate'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return [];
	});

	return typeof response === 'string' ? JSON.parse(response)['Goals'] : response['Goals'];
};

export const getFlowOfEvents = async ({
	userStoriesWithScenarios,
	useCase,
	fileName,
}: {
	userStoriesWithScenarios: string;
	useCase: IUseCase;
	fileName?: string;
}) => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.FlowOfEventsTableTemplate,
		userInput: `${i18n.t(
			'common:modelsInput.projectDescription'
		)}: ${userStoriesWithScenarios}. ${i18n.t('common:modelsInput.useCase')}: ${
			useCase.title
		}. ${i18n.t('common:modelsInput.useCaseDescription')}: ${useCase.description}.`,
		modelGroup: ChatGPTTemplateNames['UserStoriesEpicsTemplate'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return [];
	});

	const parsedResponse =
		typeof response === 'string' ? JSON.parse(response)['Steps'] : response['Steps'];

	return parsedResponse?.map((step: string) => step.replace(/\d+\./, ''));
};

// returns plantUML code of use case diagram for current use case based on user stories and data from getKeyRoles.

export const getUseCaseDiagram = async ({
	userStories,
	keyRoles,
	useCase,
	fileName,
	regenerate = false,
}: {
	userStories: string;
	keyRoles: string;
	useCase: string;
	fileName?: string;
	regenerate?: boolean;
}) => {
	const response = await getCustomData({
		templateName: regenerate
			? ChatGPTTemplateNames.RegenerateUseCaseDiagramTemplate
			: ChatGPTTemplateNames.UseCaseDiagramTemplate,
		userInput: `${i18n.t('common:modelsInput.projectDescription')}: ${userStories}. ${i18n.t(
			'common:modelsInput.keyRoles'
		)}: ${keyRoles}. ${i18n.t('common:modelsInput.useCase')}: ${useCase}`,
		aiModelEnum: ChatGPTModels['GPT4-turbo'],
		modelGroup: ChatGPTTemplateNames['UseCaseDiagramTemplate'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return '';
	});

	if (typeof response === 'string') {
		return string_between_strings('@startuml', '@enduml', response);
	} else {
		return response['Code'];
	}
};

// returns plantUML code of sequence diagram for current use case based on generated use case diagram and flowchart.

export const getSequenceDiagram = async ({
	userStoriesWithScenarios,
	keyRoles,
	mainScenario,
	useCase,
	fileName,
	regenerate = false,
}: {
	userStoriesWithScenarios: string;
	keyRoles: string;
	mainScenario: string;
	useCase: string;
	fileName?: string;
	regenerate?: boolean;
}) => {
	const response = await getCustomData({
		templateName: regenerate
			? ChatGPTTemplateNames.RegenerateSequenceDiagramTemplate
			: ChatGPTTemplateNames.SequenceDiagramTemplate,
		userInput: `${i18n.t(
			'common:modelsInput.projectDescription'
		)}: ${userStoriesWithScenarios}. ${i18n.t(
			'common:modelsInput.keyRoles'
		)}: ${keyRoles}. ${i18n.t('common:modelsInput.flowOfEvents')}: ${mainScenario}. ${i18n.t(
			'common:modelsInput.useCase'
		)}: ${useCase}`,
		aiModelEnum: ChatGPTModels['GPT4-turbo'],
		modelGroup: ChatGPTTemplateNames['UseCaseDiagramTemplate'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return '';
	});

	if (typeof response === 'string') {
		return string_between_strings('@startuml', '@enduml', response);
	} else {
		return response['Code'];
	}
};

// returns plantUML code of flowchart for current use case based on user stories and data from getFlowOfEvents.

export const getFlowDiagram = async ({
	mainScenario,
	useCase,
	fileName,
	regenerate = false,
}: {
	mainScenario: string;
	useCase: string;
	fileName?: string;
	regenerate?: boolean;
}) => {
	const response = await getCustomData({
		templateName: regenerate
			? ChatGPTTemplateNames.RegenerateFlowchartTemplate
			: ChatGPTTemplateNames.FlowchartTemplate,
		userInput: `${i18n.t('common:modelsInput.flowOfEvents')}: ${mainScenario}. ${i18n.t(
			'common:modelsInput.useCase'
		)}: ${useCase}`,
		aiModelEnum: ChatGPTModels['GPT4-turbo'],
		modelGroup: ChatGPTTemplateNames['UseCaseDiagramTemplate'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return '';
	});

	if (typeof response === 'string') {
		return string_between_strings('@startuml', '@enduml', response);
	} else {
		return response['Code'];
	}
};

// getDiagramChangeRequest parses user request to change diagram and separates request and UML type from message.
// This data will be used in getDiagramCorrection function.

export const getDiagramChangeRequest = async ({
	changeRequest,
	fileName,
}: {
	changeRequest: string;
	fileName?: string;
}) => {
	return await getCustomData({
		templateName: ChatGPTTemplateNames.DiagramChangeRequestTemplate,
		userInput: `${i18n.t('common:modelsInput.message')}: ${changeRequest}.`,
		aiModelEnum: ChatGPTModels['GPT4-turbo'],
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);

		return '';
	});
};

// getBanners receives banners with information about already existing systems similar to the one provided by user.
// Banner will be displayed on report step.

export const getBanners = async ({
	description,
	fileName,
}: {
	description: string;
	fileName?: string;
}): Promise<Array<ResponseBanner>> => {
	const response = await getCustomDataWithFormatting({
		templateName: ChatGPTTemplateNames.BannersTemplate,
		userInput: `${i18n.t('common:modelsInput.projectRequirements')}: ${description}`,
		aiModelEnum: ChatGPTModels['GPT4-turbo'],
		fileName,
	})
		.then((res) => {
			store.dispatch(fetchUserBalance());
			return res;
		})
		.catch((err) => {
			handleNotEnoughTokensError(err);

			return [];
		});

	return response?.services || response;
};

// information about getCustomStreamGptFour can be found in readme file
// in API and backend communication section as well as input parameters explanation.

export const getCustomStreamGptFour = async ({
	templateName,
	userInput,
	connectionId,
	signalName,
	modelGroup,
	aiMode = ChatGPTModels['GPT4-turbo'],
	ragTemplate = ChatGPTRagTemplateNames['RAG'],
	fileName,
}: {
	templateName: string;
	userInput: string;
	connectionId?: string;
	signalName?: string;
	aiMode?: ChatGPTModels;
	ragTemplate?: ChatGPTRagTemplateNames;
	modelGroup?: mergedTemplateType;
	fileName?: string;
}): Promise<any | void> => {
	const requestModel = modelGroup
		? store.getState().userRedux.aiRequestsModelsConfiguration[modelGroup]
		: aiMode;

	const defaultTemplate = {
		aiModelEnum: requestModel,
		userInput,
		templateName,
		signalMethodName: signalName,
		connectionId: connectionId,
		temperature: 0,
		ragTemplate,
		fileName,
	};

	const storage = new VaultService();
	const token = storage.getItem('token');

	const activeTicket = store.getState().ticketRedux.activeTicket;
	const activeStep = store.getState().ticketRedux.activeStep;

	const queryParams: Record<string, string> = {
		ticketOwnerId: activeTicket?.ticketOwnerId || '',
		ticketId: activeTicket?.id || '',
		ticketCurrentStep: activeStep.toString(),
	};

	// @ts-ignore
	const response = await chatGptFetch(
		`${baseURL}/Communication/getStreamResponse?${new URLSearchParams(queryParams).toString()}`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'text/event-stream',
				Authorization: `bearer ${token}`,
			},
			body: JSON.stringify(defaultTemplate),
		}
	);

	store.dispatch(fetchUserBalance());

	return response;
};

// getPotentialUserStoriesStream and getUserStoriesStream start stream on backend that will fire provided event with chunks of data.

export const getPotentialUserStoriesStream = async ({
	userInput,
	signalName,
	fileName,
}: {
	userInput: string;
	signalName: string;
	fileName?: string;
}) => {
	return await getCustomStreamGptFour({
		templateName: ChatGPTStreamingTemplateNames.PotentialSummaryTemplate,
		ragTemplate: ChatGPTRagTemplateNames['userStoriesRAGTemplate'],
		modelGroup: ChatGPTStreamingTemplateNames['PotentialSummaryTemplate'],
		userInput,
		signalName,
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);
		return [];
	});
};

export const getUserStoriesStream = async ({
	userInput,
	signalName,
	fileName,
}: {
	userInput: string;
	signalName: string;
	fileName?: string;
}) => {
	return await getCustomStreamGptFour({
		templateName: ChatGPTStreamingTemplateNames.SummaryTemplate,
		ragTemplate: ChatGPTRagTemplateNames['userStoriesRAGTemplate'],
		modelGroup: ChatGPTStreamingTemplateNames['PotentialSummaryTemplate'],
		userInput,
		signalName,
		fileName,
	}).catch((err) => {
		handleNotEnoughTokensError(err);
		return [];
	});
};
